import apiClient from '@/services/api'

const isCurrentRequest = (state, rootState, projectId, generation) =>
  state.projectId === projectId &&
  state.loadGeneration === generation &&
  rootState.applicationState.currentProject?.id === projectId

const hasApiError = data => data?.error_code && data.error_code !== '0'

const paymentFundingMoneySources = ['stripe_balance_only', 'stripe_balance_first', 'bank_account_only']
const paymentFundingAmountFields = [
  'payment_total_amount',
  'recipient_net_amount',
  'total_fee_amount',
  'available_balance_amount',
  'reserved_balance_amount',
  'connected_balance_gross_amount',
  'ach_debit_gross_amount'
]

const isValidPaymentFundingQuote = quote =>
  quote &&
  paymentFundingMoneySources.includes(quote.money_source) &&
  paymentFundingAmountFields.every(field => Number.isFinite(quote[field])) &&
  /^[a-z]{3}$/i.test(quote.currency)

const fundingSettingsErrorMessage = (code, fallback) => {
  const messages = {
    stripe_balance_not_eligible: 'Stripe balance payments are not available for this company.',
    bank_account_not_eligible: 'Connect and verify a company bank account before choosing this option.',
    account_debit_consent_required: 'Authorize Stripe balance debits before choosing this option.',
    account_debit_authority_required: 'Confirm that you are authorized to accept this agreement.',
    account_debit_consent_version_stale: 'The agreement changed. Reload it and review the current version.',
    money_source_invalid: 'Choose one of the available payment sources.',
    reason_required: 'Payment source could not be saved. Try again.',
    reason_too_long: 'Payment source could not be saved. Try again.'
  }
  return messages[code] || fallback
}

export default {
  async loadProjectState({ commit, state, rootState, dispatch }, { project_id } = {}) {
    const projectId = project_id ?? rootState.applicationState.currentProject?.id
    if (!projectId) {
      commit('RESET_PROJECT_STATE')
      return
    }

    commit('START_PROJECT_LOAD', projectId)
    const generation = state.loadGeneration

    try {
      const result = await dispatch('getProjectAccountStatus', { project_id: projectId })
      if (!isCurrentRequest(state, rootState, projectId, generation)) {
        return
      }

      commit('SET_ACCOUNT_RESULT', result)
      const accountCanManageBank =
        result.available &&
        result.account?.exists &&
        !result.account.needs_onboarding &&
        result.account.lifecycle === 'active'

      if (accountCanManageBank) {
        commit('SET_ACH_LOADING', true)
        try {
          const achResult = await dispatch('getProjectAchFundingStatus', { project_id: projectId })
          if (isCurrentRequest(state, rootState, projectId, generation)) {
            commit('SET_ACH_RESULT', achResult)
          }
        } catch (error) {
          if (isCurrentRequest(state, rootState, projectId, generation)) {
            commit('SET_ACH_ERROR', typeof error === 'string' ? error : error?.message)
          }
        } finally {
          if (isCurrentRequest(state, rootState, projectId, generation)) {
            commit('SET_ACH_LOADING', false)
          }
        }
      }
    } catch (error) {
      if (isCurrentRequest(state, rootState, projectId, generation)) {
        const message = typeof error === 'string' ? error : error?.message
        commit('SET_ACCOUNT_ERROR', message)
        throw error
      }
    } finally {
      if (isCurrentRequest(state, rootState, projectId, generation)) {
        commit('SET_ACCOUNT_LOADING', false)
      }
    }
  },

  async getAccountStatus(_, { owner_type, owner_id }) {
    try {
      const { data } = await apiClient.get(`/api/stripe-v2/${owner_type}/${owner_id}/account`, {
        skipSentryHttpError: true
      })
      if (hasApiError(data)) {
        throw new Error('Stripe account status could not be loaded. Try again.')
      }
      return { available: true, account: data.account }
    } catch (error) {
      if (error?.response?.status === 404) {
        return { available: false, account: null }
      }
      throw new Error('Stripe account status could not be loaded. Try again.')
    }
  },

  async getProjectAccountStatus({ dispatch }, { project_id }) {
    return dispatch('getAccountStatus', {
      owner_type: 'project',
      owner_id: project_id
    })
  },

  async getProjectAccountBalance(_, { project_id }) {
    try {
      const { data } = await apiClient.get(`/api/stripe-v2/project/${project_id}/account/balance`, {
        skipSentryHttpError: true,
        skipSentryBusinessError: true
      })
      if (hasApiError(data)) {
        const error = new Error(data.msg || 'Stripe balance could not be loaded. Try again.')
        error.code = data.code || ''
        throw error
      }
      if (!Number.isFinite(data.balance)) {
        throw new Error('Stripe returned an invalid balance.')
      }
      return data.balance
    } catch (error) {
      const responseData = error?.response?.data
      const balanceError = new Error(responseData?.msg || error?.message || 'Stripe balance could not be loaded.')
      balanceError.code = responseData?.code || error?.code || ''
      balanceError.status = error?.response?.status || error?.status
      throw balanceError
    }
  },

  async getPaymentFundingQuote(_, { payment_request_id }) {
    try {
      const { data } = await apiClient.get(`/api/stripe-v2/payment-request/${payment_request_id}/funding-quote`, {
        skipSentryHttpError: true,
        skipSentryBusinessError: true
      })
      if (hasApiError(data) || !isValidPaymentFundingQuote(data.quote)) {
        const error = new Error(data?.msg || 'Payment source details could not be loaded. Try again.')
        error.code = data?.code || ''
        throw error
      }
      return data.quote
    } catch (error) {
      const responseData = error?.response?.data
      const quoteError = new Error(
        responseData?.msg || error?.message || 'Payment source details could not be loaded. Try again.'
      )
      quoteError.code = responseData?.code || error?.code || ''
      quoteError.status = error?.response?.status || error?.status
      throw quoteError
    }
  },

  async quoteProjectBalanceDeposit(_, { project_id, amount, currency = 'usd' }) {
    try {
      const { data } = await apiClient.post(
        `/api/stripe-v2/project/${project_id}/funding/deposits/quote`,
        { amount, currency },
        { skipSentryHttpError: true, skipSentryBusinessError: true }
      )
      if (hasApiError(data) || !data.quote) {
        const error = new Error(data?.msg || 'Deposit details could not be calculated. Try again.')
        error.code = data?.code || ''
        throw error
      }
      return data.quote
    } catch (error) {
      const responseData = error?.response?.data
      const quoteError = new Error(
        responseData?.msg || error?.message || 'Deposit details could not be calculated. Try again.'
      )
      quoteError.code = responseData?.code || error?.code || ''
      quoteError.status = error?.response?.status || error?.status
      throw quoteError
    }
  },

  async createProjectBalanceDeposit(
    _,
    { project_id, request_key, amount, currency = 'usd', expected_debit_amount, expected_fee }
  ) {
    try {
      const { data } = await apiClient.post(
        `/api/stripe-v2/project/${project_id}/funding/deposits`,
        {
          request_key,
          amount,
          currency,
          expected_debit_amount,
          expected_fee
        },
        { skipSentryHttpError: true, skipSentryBusinessError: true }
      )
      if (hasApiError(data) || !data.deposit) {
        const error = new Error(data?.msg || 'The deposit could not be started. Try again with the same amount.')
        error.code = data?.code || ''
        throw error
      }
      return data.deposit
    } catch (error) {
      const responseData = error?.response?.data
      const depositError = new Error(
        responseData?.msg || error?.message || 'The deposit could not be started. Try again with the same amount.'
      )
      depositError.code = responseData?.code || error?.code || ''
      depositError.status = error?.response?.status || error?.status
      throw depositError
    }
  },

  async getProjectBalanceDeposits(_, { project_id, page = 1, size = 5 }) {
    try {
      const { data } = await apiClient.get(`/api/stripe-v2/project/${project_id}/funding/deposits`, {
        params: { page, size },
        skipSentryHttpError: true,
        skipSentryBusinessError: true
      })
      if (hasApiError(data) || !Array.isArray(data.deposits)) {
        const error = new Error(data?.msg || 'Top-up history could not be loaded. Try again.')
        error.code = data?.code || ''
        throw error
      }
      return {
        deposits: data.deposits,
        pagination: data.pagination || null
      }
    } catch (error) {
      const responseData = error?.response?.data
      const historyError = new Error(
        responseData?.msg || error?.message || 'Top-up history could not be loaded. Try again.'
      )
      historyError.code = responseData?.code || error?.code || ''
      historyError.status = error?.response?.status || error?.status
      throw historyError
    }
  },

  async getProjectAchFundingStatus(_, { funding_method_id, project_id }) {
    try {
      const { data } = await apiClient.get(`/api/stripe-v2/project/${project_id}/funding/ach`, {
        params: funding_method_id ? { funding_method_id } : undefined,
        skipSentryHttpError: true
      })
      if (hasApiError(data)) {
        throw new Error('Bank account status could not be loaded. Try again.')
      }
      return {
        available: true,
        fundingMethod: data.funding_method
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        return { available: false, fundingMethod: null }
      }
      throw new Error('Bank account status could not be loaded. Try again.')
    }
  },

  async createProjectAchSetupIntent({ commit, state, rootState }, { request_key, project_id }) {
    const generation = state.loadGeneration
    try {
      const { data } = await apiClient.post(
        `/api/stripe-v2/project/${project_id}/funding/ach/setup-intent`,
        { request_key },
        { skipSentryHttpError: true }
      )
      if (hasApiError(data)) {
        const error = new Error('Bank account setup is unavailable. Try again.')
        error.code = data.code || ''
        throw error
      }
      return data.funding_method
    } catch (error) {
      const status = error?.response?.status || error?.status
      if (status === 404 && isCurrentRequest(state, rootState, project_id, generation)) {
        commit('SET_ACH_RESULT', { available: false, fundingMethod: null })
      }
      const setupError = new Error('Bank account setup is unavailable. Try again.')
      setupError.code = error?.response?.data?.code || error?.code || ''
      setupError.status = status
      throw setupError
    }
  },

  async createProjectOnboardingLink({ commit, state, rootState }, { project_id }) {
    const generation = state.loadGeneration
    try {
      const { data } = await apiClient.post(`/api/stripe-v2/project/${project_id}/account/onboarding-link`, undefined, {
        skipSentryHttpError: true
      })
      if (hasApiError(data)) {
        throw new Error('Stripe setup could not be started. Try again.')
      }
      return {
        available: true,
        account: data.account,
        url: data.account?.onboarding?.url || null
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        if (isCurrentRequest(state, rootState, project_id, generation)) {
          commit('SET_ACCOUNT_RESULT', { available: false, account: null })
        }
        return { available: false, account: null, url: null }
      }
      throw new Error('Stripe setup could not be started. Try again.')
    }
  },

  async createProjectDashboardLink({ commit, state, rootState }, { project_id }) {
    const generation = state.loadGeneration
    try {
      const { data } = await apiClient.post(`/api/stripe-v2/project/${project_id}/account/dashboard-link`, undefined, {
        skipSentryHttpError: true
      })
      if (hasApiError(data)) {
        throw new Error('Stripe Dashboard could not be opened. Try again.')
      }
      return { available: true, url: data.dashboard_link?.url || null }
    } catch (error) {
      if (error?.response?.status === 404) {
        if (isCurrentRequest(state, rootState, project_id, generation) && state.account) {
          commit('SET_ACCOUNT_RESULT', {
            available: state.accountAvailable,
            account: { ...state.account, dashboard_available: false }
          })
        }
        return { available: false, url: null }
      }
      throw new Error('Stripe Dashboard could not be opened. Try again.')
    }
  },

  async getProjectFundingSettings(_, { project_id }) {
    try {
      const { data } = await apiClient.get(`/api/stripe-v2/project/${project_id}/account/funding-settings`, {
        skipSentryHttpError: true,
        skipSentryBusinessError: true
      })
      if (hasApiError(data) || !data.funding_settings) {
        const error = new Error(data?.msg || 'Payment source could not be loaded. Try again.')
        error.code = data?.code || ''
        throw error
      }
      return data.funding_settings
    } catch (error) {
      const responseData = error?.response?.data
      const code = responseData?.code || error?.code || ''
      const settingsError = new Error(
        fundingSettingsErrorMessage(
          code,
          responseData?.msg || error?.message || 'Payment source could not be loaded. Try again.'
        )
      )
      settingsError.code = code
      settingsError.status = error?.response?.status || error?.status
      throw settingsError
    }
  },

  async updateProjectFundingSettings(_, { project_id, money_source }) {
    try {
      const { data } = await apiClient.patch(
        `/api/stripe-v2/project/${project_id}/account/funding-settings`,
        {
          money_source,
          reason: 'Company funding preference'
        },
        { skipSentryHttpError: true, skipSentryBusinessError: true }
      )
      if (hasApiError(data) || !data.funding_settings) {
        const error = new Error(data?.msg || 'Payment source could not be saved. Try again.')
        error.code = data?.code || ''
        throw error
      }
      return data.funding_settings
    } catch (error) {
      const responseData = error?.response?.data
      const code = responseData?.code || error?.code || ''
      const settingsError = new Error(
        fundingSettingsErrorMessage(
          code,
          responseData?.msg || error?.message || 'Payment source could not be saved. Try again.'
        )
      )
      settingsError.code = code
      settingsError.status = error?.response?.status || error?.status
      throw settingsError
    }
  },

  async acceptProjectDebitConsent(_, { project_id, text_version, authority_confirmed, money_source }) {
    try {
      const { data } = await apiClient.post(
        `/api/stripe-v2/project/${project_id}/account/debit-consent/accept`,
        {
          text_version,
          authority_confirmed,
          money_source
        },
        { skipSentryHttpError: true, skipSentryBusinessError: true }
      )
      if (hasApiError(data) || !data.funding_settings || !data.debit_consent) {
        const error = new Error(data?.msg || 'Stripe balance authorization could not be saved. Try again.')
        error.code = data?.code || ''
        throw error
      }
      return {
        fundingSettings: data.funding_settings,
        debitConsent: data.debit_consent
      }
    } catch (error) {
      const responseData = error?.response?.data
      const code = responseData?.code || error?.code || ''
      const consentError = new Error(
        fundingSettingsErrorMessage(
          code,
          responseData?.msg || error?.message || 'Stripe balance authorization could not be saved. Try again.'
        )
      )
      consentError.code = code
      consentError.status = error?.response?.status || error?.status
      throw consentError
    }
  },

  async revokeProjectDebitConsent(_, { project_id, reason }) {
    try {
      const { data } = await apiClient.post(
        `/api/stripe-v2/project/${project_id}/account/debit-consent/revoke`,
        { reason },
        { skipSentryHttpError: true, skipSentryBusinessError: true }
      )
      if (hasApiError(data) || !data.funding_settings || !data.debit_consent) {
        const error = new Error(data?.msg || 'Stripe balance authorization could not be revoked. Try again.')
        error.code = data?.code || ''
        throw error
      }
      return {
        fundingSettings: data.funding_settings,
        debitConsent: data.debit_consent
      }
    } catch (error) {
      const responseData = error?.response?.data
      const revokeError = new Error(
        responseData?.msg || error?.message || 'Stripe balance authorization could not be revoked. Try again.'
      )
      revokeError.code = responseData?.code || error?.code || ''
      revokeError.status = error?.response?.status || error?.status
      throw revokeError
    }
  },

  async getPlatformSettings(_, { project_id }) {
    try {
      const { data } = await apiClient.get(`/api/stripe-v2/project/${project_id}/platform-settings`, {
        skipSentryHttpError: true
      })
      if (hasApiError(data)) {
        throw new Error('Billing settings could not be loaded. Try again.')
      }
      return { available: true, settings: data.platform_settings }
    } catch (error) {
      if (error?.response?.status === 404) {
        return { available: false, settings: null }
      }
      throw new Error('Billing settings could not be loaded. Try again.')
    }
  },

  async updatePlatformSettings(_, { project_id, current_model, connect_billing_model }) {
    try {
      const { data } = await apiClient.patch(`/api/stripe-v2/project/${project_id}/platform-settings`, {
        expected_connect_billing_model: current_model,
        connect_billing_model,
        confirm_tax_responsibility_change: true
      })
      if (hasApiError(data)) {
        throw new Error('Billing model could not be changed. Reload the page and try again.')
      }
      return data.platform_settings
    } catch (error) {
      throw new Error('Billing model could not be changed. Reload the page and try again.')
    }
  },

  async getIntercompanyInvoice(_, { payment_request_id }) {
    try {
      const { data } = await apiClient.get(
        `/api/stripe-v2/payment-request/${payment_request_id}/intercompany-invoice`,
        { skipSentryHttpError: true }
      )
      if (hasApiError(data)) {
        throw new Error('ACH payment status could not be loaded.')
      }
      return data.invoice
    } catch (error) {
      if (error?.response?.status === 404) {
        return null
      }
      throw new Error('ACH payment status could not be loaded.')
    }
  }
}
